"use client";
import React, { useState, useEffect } from 'react';
import TemplateSettingsDialog from './TemplateSettingsDialog.tsx';
import { Block } from '../../interfaces.ts';
import LocaleFieldWrapper from '../../components/LocaleFieldWrapper.tsx';
import { useLocale } from '../../context/localeContext.tsx';

interface ContactBlockTemplateSettingsDialogProps {
  block: Block;
  onClose: () => void;
  onSave: (updatedBlock: Block) => void;
  availableLocales: string[];
  fallbackLocale?: string;
}

const ContactBlockTemplateSettingsDialog: React.FC<ContactBlockTemplateSettingsDialogProps> = ({ 
  block, 
  onClose, 
  onSave,
  availableLocales
}) => {
  const { activeLocale } = useLocale();
  
  // Local state for header text (not part of context)
  const [localeHeaderText, setLocaleHeaderText] = useState<{
    [key: string]: string;
  }>(
    availableLocales.reduce((acc: any, locale: string) => {
      acc[locale] = block.defaultContent?.[locale]?.headerText || '';
      return acc;
    }, {})
  );

  const [withHeader, setWithHeader] = useState(block.properties.withHeader === undefined ? true : block.properties.withHeader);
  const [headerLevel, setHeaderLevel] = useState(block.properties.headerLevel || 2);

  useEffect(() => {
    const currentHeaderText = localeHeaderText[activeLocale];
    if (!currentHeaderText) {
      setLocaleHeaderText((prev) => ({
        ...prev,
        [activeLocale]: '',
      }));
    }
  }, [activeLocale, localeHeaderText]);

  const handleSave = () => {
    onSave({
      ...block,
      properties: {
        ...block.properties,
        withHeader,
        headerLevel
      },
      defaultContent: withHeader ? {
        ...block.defaultContent,
        [activeLocale]: {
          headerText: localeHeaderText[activeLocale]
        }
      } : block.defaultContent
    });
  };

  const handleHeaderTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocaleHeaderText((prev) => ({
      ...prev,
      [activeLocale]: e.target.value,
    }));
  };

  return (
    <TemplateSettingsDialog title="Contact Block Settings" onClose={onClose} onSave={handleSave}>
      <div className="mt-4">
        <label className="block mb-2">
          <input
            type="checkbox"
            className="mr-2"
            checked={withHeader}
            onChange={(e) => setWithHeader(e.target.checked)}
          />
          With Header
        </label>
      </div>
      
      {withHeader && (
        <>
          <div className="mt-4">
            <LocaleFieldWrapper>
              <label className="block mb-2">
                Header Text ({activeLocale.toUpperCase()}):
                <input
                  type="text"
                  className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
                  value={localeHeaderText[activeLocale] || ''}
                  onChange={handleHeaderTextChange}
                />
              </label>
            </LocaleFieldWrapper>
          </div>
          
          <div className="mt-4">
            <label className="block mb-2">
              Header Level:
              <select
                className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
                value={headerLevel}
                onChange={(e) => setHeaderLevel(parseInt(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6].map((level) => (
                  <option key={level} value={level}>
                    H{level}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
    </TemplateSettingsDialog>
  );
};

export default ContactBlockTemplateSettingsDialog;